import axiosLib, { AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import _ from 'lodash';
import helper from '@/services/helper';
import to from 'await-to-js';
import store from '@/store';
import router from '@/router';
import { i18n } from '@/i18n-setup';
import { _encrypt } from '@/services/encryption';

const axios = axiosLib.create({});

const api = {
  Init() {
    const url = '/api/config';
    return axiosWrapper<ConfigResponse>({ method: 'GET', url });
  },
  SetLanguage(lang: string) {
    const url = '/api/SetLanguage/' + lang;
    return axiosWrapper<ResponseBase>({ method: 'POST', url });
  },
  GetUserInfo(Username: string, tocken: string) {
    const url = '/api/GetVoterInfo';
    const data = { Username, Password: tocken };
    return axiosWrapper<GetInfoResponse>({ method: 'POST', url, data });
  },
  GetVoteSubjects() {
    const url = '/api/GetVoteSubjects';
    return axiosWrapper<GetSubjectsResponse>({ method: 'GET', url });
  },
  ResetPassword(Username: string) {
    const url = '/api/GenerateCode';
    const data = { Username };
    return axiosWrapper<GenerateCodeResponse>({ method: 'POST', url, data });
  },
  SetCanVoteAndGetUserData(Username: string) {
    const url = '/api/SetCanVoteAndGetUserData';
    const data = { Username };
    return axiosWrapper<GetInfoResponse>({ method: 'POST', url, data });
  },
  VoterLogin(username: string, password: string) {
    const url = '/api/VoterLogin';
    var data = { Username: username, Password: password };
    return axiosWrapper<VoterLoginResponse>({ method: 'POST', url, data });
  },
  StationLogin(username: string, password: string, tocken: string) {
    const url = '/api/StationLogin';
    var data = { Username: username, Password: password, tocken };
    return axiosWrapper<StationLoginResponse>({ method: 'POST', url, data });
  },
  VoterAssign(username: string, password: string) {
    const url = '/api/SaAssign';
    var data = { Username: username, Password: password };
    return axiosWrapper<VoterLoginResponse>({ method: 'POST', url, data });
  },
  Logout(password?: string) {
    const url = process.env.VUE_APP_MODE === 'WEB' ? '/api/VoterLogout' : '/api/StationLogout';
    var data = { Password: password };
    return axiosWrapper<ResponseBase>({ method: 'POST', url, data });
  },
  CheckPassword(password: string) {
    const url = '/api/CheckPassword';
    var data = { Password: password };
    return axiosWrapper<ResponseBase>({ method: 'POST', url, data });
  },
  CancelVote() {
    const url = '/api/CancelVote';
    return axiosWrapper<ResponseBase>({ method: 'POST', url });
  },
  StationCancelVote(password: string) {
    const url = '/api/StationCancelVote';
    return axiosWrapper<ResponseBase>({ method: 'POST', url, data: { password } });
  },
  GetVoteData(subject?: string) {
    const url = '/api/GetVoteData';
    return axiosWrapper<GetVoteDataResponse>({ method: 'POST', url, params: {subject} });
  },
  CheckElectionDates() {
    const url = '/api/CheckElectionDates';
    return axiosWrapper<any>({ method: 'GET', url });
  },
  GetLastPrintLines() {
    const url = '/api/LastPrintLines';
    return axiosWrapper<PrinterLinesResponse>({ method: 'GET', url });
  },
  async VoteSave(saveData: EndVoteItemModel[]) {
    const url = '/api/EndVote';
    let plain = { VoterData: saveData };
    let encrypted = await _encrypt(JSON.stringify(plain));
    let data = { Plain: null, Enc: encrypted };
    return await axiosWrapper<PrinterLinesResponse>({ method: 'POST', url, data });
  },
  async SubmitIssue(issue: VoterIssue, tocken: string) {
    const url = `/api/Issue`;
    let data = { issue, tocken };
    return await axiosWrapper<SaveIssueResponse>({ method: 'POST', url, data });
  },
  async LoadIssue(tocken: string) {
    const url = `/api/Issue/${tocken}`;
    return await axiosWrapper<LoadIssueResponse>({ method: 'GET', url });
  },
  async SubmitIssueMessage(message: string, tocken: string) {
    const url = `/api/Issue/${tocken}/Message`;
    let data = { message };
    return await axiosWrapper<LoadIssueResponse>({ method: 'POST', url, data });
  },
};

export default api;

axios.interceptors.response.use(
  (response: any) => {
    return response;
  },
  (error: AxiosError<any>) => {
    if (error.response && error.response.status === 401) {
      return new Promise(async function(resolve) {
        await helper.swalWarning('Unauthorized');
        store.commit('UnAuthorize');
        router.push({ name: 'login' });
        resolve({ data: { Error: null } });
      });
    }
    if (error.response?.status === 403) {
      return { data: { Error: i18n.t('general.permission.accessdenied') } };
    }
    if (error.response?.status === 404) {
      return { data: { Error: i18n.t('general.permission.invalidrequest') } };
    }
    if (error.response?.status === 408) {
      return { data: { Error: i18n.t('general.permission.requestyimeout') } };
    }

    const customError = new AxiosCustomError<any>(error);
    customError.Error = 'Server Error';
    return Promise.reject(customError);
  },
);

class AxiosCustomError<T> implements AxiosError<T> {
  config: AxiosRequestConfig;
  code?: string;
  request?: any;
  response?: AxiosResponse<T>;
  isAxiosError: boolean;
  name: string;
  message: string;
  stack?: string;

  Error?: string;

  public constructor(axiosErr: AxiosError<T>) {
    this.config = axiosErr.config;
    this.code = axiosErr.code;
    this.request = axiosErr.request;
    this.response = axiosErr.response;
    this.isAxiosError = axiosErr.isAxiosError;
    this.name = axiosErr.name;
    this.message = axiosErr.message;
    this.stack = axiosErr.stack;
  }
  toJSON() {
    return this;
  }
}

export class ApiResult<T extends ResponseBase> {
  readonly Error: AxiosCustomError<T> | null;
  readonly Response: AxiosResponse<T> | undefined;
  readonly ErrorMessage: string | null;
  public readonly IsNoConnection: boolean;

  get ResponseData(): [T | null, string | null] {
    return [(this.Response && this.Response.data) || null, this.ErrorMessage];
  }

  constructor(err: AxiosCustomError<T> | null, res: AxiosResponse<T> | undefined) {
    var self = this;
    this.Error = err;
    this.Response = res;

    this.IsNoConnection = (() => {
      if (!self.Error) return false;
      return self.Error.request && !self.Error.response;
    })();

    this.ErrorMessage = (() => {
      if (self.IsNoConnection) return i18n.t('api.nointernet') as string;
      if (self.Error) return 'Server Error';
      if (self.Response?.data?.Error) return self.Response.data.Error;
      return null;
    })();
  }

  async getData(
    waitError = false,
    onError: ((code: number | undefined) => void) | null = null,
    skipStatus: number | null = null,
  ) {
    let p = null;
    if (this.IsNoConnection) {
      p = helper.swalError(i18n.t('api.nointernet') as string);
      if (waitError) await p;
    } else {
      const [data, error] = this.ResponseData;
      if (error) {
        var text: string | undefined = undefined;
        let html: string | undefined = undefined;
        switch (Number(data?.ErrorCode)) {
          case 10:
            html = i18n.t(error, [
              `<a href="mailto:${store.state.CommitteeEmail}">${store.state.CommitteeEmail}</a>`,
            ]) as string;
            break;
          case 11:
            html = i18n.t(error, [
              `<a href="/#/help" target="_blank"><b class="underline">${i18n.t('vs.api.error.PersonVoteInProgressWEB.here')}</b></a>`
            ]) as string;
            break;
          case 5:
            text = i18n.t(error) as string;
            break;
          case 15:
            return data;
          case skipStatus:
            return data;
          default:
            text = error;
            break;
        }
        p = helper.swalWarning({
          html,
          text,
          customClass: {
            content: 'ws-preline',
          },
        });
        if (waitError) await p;
        if(onError) onError(data?.ErrorCode);
      } else return data;
    }

    return null;
  }
  async getDataNoPopup() {
    let msg: string | null = 'Server error';
    if (this.IsNoConnection) {
      msg = i18n.t('api.nointernet') as string;
    } else {
      let data;
      [data, msg] = this.ResponseData;
      if (!msg) return data;
    }
    return { Error: msg };
  }
}

async function axiosWrapper<T extends ResponseBase>(req: AxiosRequestConfig): Promise<ApiResult<T>> {
  const promise = axios(req);
  const [err, axiosResponse] = await to(promise);
  if (!err) {
    return new ApiResult<T>(null, axiosResponse);
  }
  var customError: AxiosCustomError<T> | null = null;
  if (err instanceof AxiosCustomError) {
    customError = err;
  } else if ('isAxiosError' in err) {
    customError = new AxiosCustomError(err);
  } else {
    // this should never happen!!
  }
  return new ApiResult<T>(customError, axiosResponse);
}
